import { Card, NeutralTag, SuccessTag, Text } from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { Button } from "react-aria-components";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { BillingProfilesActionDropdownMenu } from "./BillingProfilesActionsDropdownMenu";

interface Props {
  billingProfileName: string | null;
  billingProfileId: string;
  companyName: string | null;
  currency: string;
  groups: readonly { id: string; name: string }[];
  archived: boolean;
}
export function TableDataCard(props: Props) {
  const {
    billingProfileName,
    billingProfileId,
    companyName,
    currency,
    groups,
    archived,
  } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Card className={styles.card}>
      <div className={styles.container}>
        <Button
          onPress={() => {
            navigate(`/billing/billing-profiles/${billingProfileId}`, {
              state: {
                last: { href: `${location.pathname}${location.search}` },
              },
            });
          }}
          className={styles.userDetailsContainer}
          data-testid="sm-user-selectable-container"
        >
          <div className={styles.userDetails}>
            <Text
              style="global/heading-medium"
              testID="sm-billing-profile-name"
            >
              {billingProfileName || "--"}
            </Text>
          </div>
        </Button>
        <BillingProfilesActionDropdownMenu
          profileId={billingProfileId || ""}
          archived={archived}
        />
      </div>
      <div className={styles.columns}>
        <div className={css(styles.column, { flexBasis: "60%" })}>
          <Text style="global/label-small" color="text-weak">
            {t("Company name")}
          </Text>
          <Text>{companyName}</Text>
        </div>
        <div className={css(styles.column, { flexBasis: "60%" })}>
          <Text style="global/label-small" color="text-weak">
            {t("Currency")}
          </Text>
          <Text>{currency}</Text>
        </div>
      </div>
      <div className={styles.columns}>
        <div className={styles.column}>
          <Text style="global/label-small" color="text-weak">
            {t("Groups")}
          </Text>
          <Text>{groups.map((group) => group.name).join(", ") || "--"}</Text>
        </div>
        <div className={styles.column}>
          <Text style="global/label-small" color="text-weak">
            {t("Status")}
          </Text>
          {archived ? (
            <NeutralTag type="subtle">{t("Archived")}</NeutralTag>
          ) : (
            <SuccessTag type="subtle">{t("Active")}</SuccessTag>
          )}
        </div>
      </div>
    </Card>
  );
}

const styles = {
  card: css({
    padding: "var(--space-4)",
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-4)",
    borderRadius: "var(--border-radius-3)",
  }),
  container: css({
    display: "flex",
    justifyContent: "space-between",
  }),
  userDetailsContainer: css({
    display: "flex",
    alignItems: "center",
    gap: 10,
    cursor: "pointer",
    backgroundColor: "transparent",
    borderWidth: 0,
    paddingRight: 0,
    paddingLeft: 0,
  }),
  userDetails: css({
    display: "flex",
    flexDirection: "column",
    textAlign: "left",
  }),
  columns: css({
    display: "flex",
    flexDirection: "row",
    gap: "var(--space-2)",
  }),
  column: css({
    display: "flex",
    flexDirection: "column",
  }),
};
